import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError, type U2000ApiErrorResult } from '../../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../../u2000Core/context/toast/U2000ToastContext';
import { NS } from '../U3204';
import {
  doGetU2200Job,
  luHandleAppiError,
  u3204ParseU2200JobResponse,
  type U3204U2200Job,
} from '../U3204Service';

const POLL_INTERVAL_MS = 2500;

function isTerminalStatus(status: string): boolean {
  return status === 'completed' || status === 'failed';
}

export function useU3204PollU2200JobHandlers() {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation(NS);
  const toast = useToast();
  const timerRef = useRef<number | null>(null);
  const activeJobIdRef = useRef<string | null>(null);
  const cancelledRef = useRef(false);

  const clearPoll = useCallback(() => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    activeJobIdRef.current = null;
  }, []);

  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      clearPoll();
    };
  }, [clearPoll]);

  const getJob = useCallback(
    async (jobId: string): Promise<U3204U2200Job | null> => {
      try {
        const response = await doGetU2200Job(axios, jobId);
        return u3204ParseU2200JobResponse(response.data);
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError) as U2000ApiErrorResult;
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return null;
      }
    },
    [axios, t, toast],
  );

  const pollUntilDone = useCallback(
    (jobId: string, onUpdate: (job: U3204U2200Job) => void): Promise<U3204U2200Job | null> => {
      clearPoll();
      cancelledRef.current = false;
      activeJobIdRef.current = jobId;

      return new Promise((resolve) => {
        const tick = async () => {
          if (cancelledRef.current || activeJobIdRef.current !== jobId) {
            resolve(null);
            return;
          }
          const job = await getJob(jobId);
          if (cancelledRef.current || activeJobIdRef.current !== jobId) {
            resolve(null);
            return;
          }
          if (!job) {
            clearPoll();
            resolve(null);
            return;
          }
          onUpdate(job);
          if (isTerminalStatus(job.status)) {
            clearPoll();
            resolve(job);
            return;
          }
          timerRef.current = window.setTimeout(() => {
            void tick();
          }, POLL_INTERVAL_MS);
        };
        void tick();
      });
    },
    [clearPoll, getJob],
  );

  return { pollUntilDone, clearPoll };
}
